import { useMemberReviewList, ReviewInfo, ReviewCoachInfo, ReviewMembershipInfo } from '@/features/centerReview';
import { getItemWithExpiration } from '@/lib/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Col, Row } from '@/lib/utils';
import { MediumText, RegularText } from '@/lib/Text';
import { colors } from '@/lib/colors';
import Dropdown from '@/components/DropDown/DropDown';
import MemberSearchTextInput from '@/components/Button/MemberSearchTextInput';
import Table from '@/components/Table/Table';
import dayjs from 'dayjs';
import SvgIcon from '@/components/SvgIcon';
import ModalMemberReview from './ModalMemberReview';

// 회차 옵션 상수
const STAGE_OPTIONS = [
    { code: 'first_class', value: '첫 회차' },
    { code: 'middle_class', value: '중간 회차' },
    { code: 'final_class', value: '마지막 회차' },
];

// 페이지 사이즈 옵션 상수
const PAGE_SIZE_OPTIONS = [
    { code: '10', value: '10줄씩 보기' },
    { code: '20', value: '20줄씩 보기' },
    { code: '30', value: '30줄씩 보기' },
];

function CenterMemberReviewsPage() {
    const [authObject] = useState(getItemWithExpiration('authObject'));

    // API 데이터 fetch
    const { data, isLoading } = useMemberReviewList({
        centerId: authObject?.centerId,
        enabled: true,
    });

    // 필터 상태
    const [selectedCoach, setSelectedCoach] = useState<{ code: string; value: string }>({
        code: 'all',
        value: '모든 강사',
    });
    const [selectedMembership, setSelectedMembership] = useState<{ code: string; value: string }>({
        code: 'all',
        value: '모든 수강권',
    });
    const [selectedStage, setSelectedStage] = useState<{ code: string; value: string }>({
        code: 'all',
        value: '회차 기준',
    });
    const [selectedPageSize, setSelectedPageSize] = useState<{ code: string; value: string }>(PAGE_SIZE_OPTIONS[0]);
    const [searchText, setSearchText] = useState<string>('');

    // 모달 상태
    const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);

    // 알림센터에서 reviewId 쿼리로 진입 시 해당 리뷰 모달 자동 오픈
    const [searchParams, setSearchParams] = useSearchParams();
    const reviewIdFromUrl = searchParams.get('reviewId');
    useEffect(() => {
        if (reviewIdFromUrl) {
            setSelectedReviewId(reviewIdFromUrl);
        }
    }, [reviewIdFromUrl]);

    // 드롭다운 옵션 생성
    const coachOptions = useMemo(
        () =>
            data?.reviewCoachList?.map((coach: ReviewCoachInfo) => ({
                code: coach.coachId,
                value: coach.coachName,
            })) || [],
        [data?.reviewCoachList],
    );

    const membershipOptions = useMemo(
        () =>
            data?.reviewMembershipList?.map((membership: ReviewMembershipInfo) => ({
                code: membership.membershipId,
                value: membership.membershipName,
            })) || [],
        [data?.reviewMembershipList],
    );

    // 검색 옵션 (회원명 리스트)
    const memberSearchOptions = useMemo(() => {
        const uniqueMembers = new Map<string, { code: string; value: string }>();
        data?.reviewList?.forEach((review: ReviewInfo) => {
            if (!uniqueMembers.has(review.memberId)) {
                uniqueMembers.set(review.memberId, {
                    code: review.memberId,
                    value: review.memberAlias,
                });
            }
        });
        return Array.from(uniqueMembers.values());
    }, [data?.reviewList]);

    // 필터링된 데이터
    const filteredData = useMemo(() => {
        let filtered: ReviewInfo[] = data?.reviewList || [];

        // 강사 필터
        if (selectedCoach.code !== 'all') {
            filtered = filtered.filter((review: ReviewInfo) => review.coachName === selectedCoach.value);
        }

        // 수강권 필터 (reviewList에 membershipId가 없으므로 수강권명으로 필터링하려면 별도 API 필요)
        // 현재 API 응답에 membershipId가 없으므로 이 필터는 스킵
        // if (selectedMembership.code !== 'all') {
        //     filtered = filtered.filter((review: ReviewInfo) => review.membershipId === selectedMembership.code);
        // }

        // 회차 필터
        if (selectedStage.code !== 'all') {
            filtered = filtered.filter((review: ReviewInfo) => review.reviewStage === selectedStage.code);
        }

        // 검색 필터
        if (searchText) {
            filtered = filtered.filter((review: ReviewInfo) => review.memberAlias.includes(searchText));
        }

        return filtered;
    }, [data?.reviewList, selectedCoach, selectedStage, searchText]);

    // 테이블 컬럼 정의
    const columns = useMemo(
        () => [
            {
                id: 'memberAlias',
                header: '회원',
                accessorKey: 'memberAlias',
                cell: (info: any) => (
                    <RegularText fontSize={16} lineHeight={24} color={colors.Text_1}>
                        {info.getValue()}
                    </RegularText>
                ),
                size: 150,
                enableSorting: false,
            },
            {
                id: 'coachName',
                header: '담당 강사',
                accessorKey: 'coachName',
                cell: (info: any) => (
                    <RegularText fontSize={16} lineHeight={24} color={colors.Text_1}>
                        {info.getValue()}
                    </RegularText>
                ),
                size: 150,
                enableSorting: false,
            },
            {
                id: 'reviewStage',
                header: '회차',
                accessorKey: 'reviewStage',
                cell: (info: any) => {
                    const sessionInfo = `${info.row.original.reviewSession}/${info.row.original.reviewTotalSession}`;
                    return (
                        <RegularText fontSize={16} lineHeight={24} color={colors.Text_1}>
                            {info.row.original.reviewStageName}({sessionInfo})
                        </RegularText>
                    );
                },
                size: 200,
                enableSorting: false,
            },
            {
                id: 'reviewAverageRating',
                header: ({ column }: any) => {
                    return (
                        <div
                            className="flex items-center justify-center gap-[4px] cursor-pointer select-none"
                            onClick={column.getToggleSortingHandler()}
                        >
                            <RegularText fontSize={16} lineHeight={24} color={colors.Text_1}>
                                평균 별점
                            </RegularText>
                        </div>
                    );
                },
                accessorKey: 'reviewAverageRating',
                cell: (info: any) => {
                    const rating = info.getValue();
                    return (
                        <Row className="items-center justify-center gap-[8px]">
                            <SvgIcon name="SvgStar" fill="#3D73DD" size={16} />
                            <RegularText fontSize={16} lineHeight={24} color={colors.Text_1}>
                                {rating.toFixed(1)}
                            </RegularText>
                        </Row>
                    );
                },
                size: 150,
                enableSorting: true,
            },
            {
                id: 'reviewCreatedAt',
                header: ({ column }: any) => {
                    return (
                        <div
                            className="flex items-center justify-center gap-[4px] cursor-pointer select-none"
                            onClick={column.getToggleSortingHandler()}
                        >
                            <RegularText fontSize={16} lineHeight={24} color={colors.Text_1}>
                                평가 일시
                            </RegularText>
                        </div>
                    );
                },
                accessorKey: 'reviewCreatedAt',
                cell: (info: any) => {
                    const date = dayjs(info.getValue()).format('YYYY-MM-DD(ddd) HH:mm');
                    return (
                        <RegularText fontSize={16} lineHeight={24} color={colors.Text_1}>
                            {date}
                        </RegularText>
                    );
                },
                size: 250,
                enableSorting: true,
            },
            {
                id: 'reviewScheduleStartTime',
                header: ({ column }: any) => {
                    return (
                        <div
                            className="flex items-center justify-center gap-[4px] cursor-pointer select-none"
                            onClick={column.getToggleSortingHandler()}
                        >
                            <RegularText fontSize={16} lineHeight={24} color={colors.Text_1}>
                                수업 일시
                            </RegularText>
                        </div>
                    );
                },
                accessorKey: 'reviewScheduleStartTime',
                cell: (info: any) => {
                    const date = dayjs(info.getValue()).format('YYYY-MM-DD(ddd) HH:mm');
                    return (
                        <RegularText fontSize={16} lineHeight={24} color={colors.Text_1}>
                            {date}
                        </RegularText>
                    );
                },
                size: 250,
                enableSorting: true,
            },
        ],
        [],
    );

    const handleRowClick = (row: ReviewInfo) => {
        setSelectedReviewId(row.reviewId);
    };

    const handleCloseModal = () => {
        setSelectedReviewId(null);
        if (searchParams.has('reviewId')) {
            searchParams.delete('reviewId');
            setSearchParams(searchParams, { replace: true });
        }
    };

    const handleSearchSelect = (selected: { code: string; value: string }) => {
        if (selected.code === 'all') {
            setSearchText('');
        } else {
            setSearchText(selected.value);
        }
    };

    if (isLoading) {
        return (
            <Col className="w-full h-screen items-center justify-center">
                <RegularText fontSize={16} lineHeight={21} color={colors.Text_2}>
                    불러오는 중...
                </RegularText>
            </Col>
        );
    }

    return (
        <Col className="w-full h-full px-[30px] py-[34px] bg-white rounded-[21px]">
            {/* 헤더 */}
            <Row className="items-center justify-between mb-[30px]">
                <RegularText fontSize={20} lineHeight={28} color={'#191F28'}>
                    총 {data?.totalReviewMemberCount || 0}명의 리뷰
                </RegularText>
                <Row className="items-center gap-[12px] ">
                    <Dropdown
                        options={coachOptions}
                        onSelect={setSelectedCoach}
                        totalText="모든 강사"
                        isAllOption={true}
                        className="h-[36px]"
                        initialSelected={selectedCoach.code}
                    />
                    <Dropdown
                        options={membershipOptions}
                        onSelect={setSelectedMembership}
                        totalText="모든 수강권"
                        isAllOption={true}
                        className="h-[36px]"
                        initialSelected={selectedMembership.code}
                    />
                    <Dropdown
                        options={STAGE_OPTIONS}
                        onSelect={setSelectedStage}
                        totalText="회차 기준"
                        isAllOption={true}
                        className="h-[36px]"
                        initialSelected={selectedStage.code}
                    />
                    <Dropdown
                        options={PAGE_SIZE_OPTIONS}
                        onSelect={setSelectedPageSize}
                        isAllOption={false}
                        className="h-[36px]"
                        initialSelected={selectedPageSize.code}
                    />
                    <MemberSearchTextInput
                        data={memberSearchOptions}
                        placeholder="회원 이름 검색"
                        onSelect={handleSearchSelect}
                        width={154}
                        height={36}
                        onSearch={setSearchText}
                    />
                </Row>
            </Row>

            {/* 필터 영역 */}

            {/* 테이블 */}
            <Table
                data={filteredData}
                columns={columns}
                isPagination={true}
                pageSize={parseInt(selectedPageSize.code)}
                height="calc(100vh - 300px)"
                rowHeight={84}
                onRowClick={handleRowClick}
                emptyComponent={
                    <Col className="w-full h-full mt-[227px] items-center justify-center">
                        <RegularText fontSize={20} lineHeight={24} color={colors.Text_1}>
                            등록된 회원 리뷰가 없어요🤔
                        </RegularText>
                    </Col>
                }
                isIndex={false}
                headerColor="white"
                headerAndCellPadding={50}
                isHedaerDownBorder
                customPaginationComponent={({
                    nextPage,
                    canNextPage,
                    previousPage,
                    canPreviousPage,
                    pageIndex,
                    totalPageIndex,
                    setPageIndex,
                }) => {
                    const totalPages = totalPageIndex || 0;
                    const currentPage = (pageIndex || 0) + 1;
                    return (
                        <Row className="flex items-center self-center my-[22px]">
                            <Row className="flex items-center gap-[30px] mr-[30px]">
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setPageIndex?.(i)}
                                        className={`w-[32px] h-[32px] rounded-full flex items-center justify-center ${
                                            currentPage === i + 1 ? 'bg-[#3D4244]' : 'hover:bg-[#F0F0F0]'
                                        }`}
                                    >
                                        <MediumText
                                            fontSize={16}
                                            lineHeight={21}
                                            color={currentPage === i + 1 ? '#FFFFFF' : colors.Text_1}
                                        >
                                            {i + 1}
                                        </MediumText>
                                    </button>
                                ))}
                            </Row>
                            <button
                                onClick={previousPage}
                                disabled={canPreviousPage}
                                className="disabled:opacity-30 disabled:cursor-not-allowed mr-[37px]"
                            >
                                <SvgIcon
                                    name={'SvgLeftStrokeArrow'}
                                    size={15}
                                    fill={colors.trans}
                                    stroke={'#70747C'}
                                    strokeWidth={1.2}
                                />
                            </button>

                            <button
                                onClick={nextPage}
                                disabled={canNextPage}
                                className="disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                <SvgIcon
                                    name={'SvgRightStrokeArrow'}
                                    size={15}
                                    fill={colors.trans}
                                    stroke={'#70747C'}
                                    strokeWidth={1.2}
                                />
                            </button>
                        </Row>
                    );
                }}
            />

            {/* 모달 */}
            {selectedReviewId && <ModalMemberReview reviewId={selectedReviewId} unmount={handleCloseModal} />}
        </Col>
    );
}

export default CenterMemberReviewsPage;
